import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';

/** How a router navigation was triggered. Mirrors `NavigationStart.navigationTrigger`
    (the type is not exported from `@angular/router`, so it is declared here). */
export type NavTrigger = 'imperative' | 'popstate' | 'hashchange';

/**
 * Tracks the in-app navigation URL stack so breadcrumb "up" navigation can go BACK
 * to an ancestor that is already behind the current page in browser history, instead
 * of PUSHING a fresh copy of it.
 *
 * Why this exists — the breadcrumb back bug:
 * The breadcrumb back arrow is history-based (`Location.back()`), but a breadcrumb
 * crumb LINK used to be a plain `routerLink`, i.e. a history PUSH. So returning "up"
 * to a parent crumb (Workspace) from a child page (Update Details) pushed a *new*
 * Workspace entry ON TOP of the child — leaving the child as the entry directly
 * behind it. The next history-based Back then walked straight back into the child
 * (`Dashboard → Workspace → Update Details → back → Workspace → back → Update Details`).
 * The two affordances disagreed: crumb = push, arrow = pop.
 *
 * The stack mirrors the browser's back history: a forward navigation appends; a
 * back/forward (`popstate`) realigns to the matched entry (dropping anything after
 * it). {@link stepsBackTo} then answers "is this crumb already behind me, and by how
 * many steps?" so the caller can `Location.historyGo(-n)` (a true pop) rather than
 * grow the stack — see {@link AppComponent.goToCrumb}.
 *
 * Browser-only: the SSR server does no in-app navigation and its per-request injector
 * holds a throwaway stack.
 */
@Injectable({ providedIn: 'root' })
export class NavigationHistoryService {
  private readonly router = inject(Router);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  /** Visited URLs, oldest first; the last entry is the current page. Mirrors the
      browser's back stack up to the current position. */
  private readonly stack: string[] = [];

  /** How the pending navigation was triggered — captured from `NavigationStart`
      because `NavigationEnd` does not carry it. Distinguishes a forward push
      (`imperative`/`hashchange`) from a back/forward (`popstate`). */
  private lastTrigger: NavTrigger = 'imperative';

  /** Whether the pending navigation was issued with `replaceUrl` — captured from the
      in-flight navigation's extras at `NavigationStart`, because `NavigationEnd` does not
      carry it and `getCurrentNavigation()` is already null by the time {@link record} runs.
      A `replaceUrl` navigation SWAPS the current browser-history entry rather than pushing a
      new one (e.g. a tab switch inside a tabbed page, or the message-view bounce), so the
      stack must overwrite its top to stay aligned with real history. */
  private lastReplaceUrl = false;

  constructor() {
    if (!this.isBrowser) {
      return;
    }
    this.router.events.pipe(takeUntilDestroyed()).subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.lastTrigger = event.navigationTrigger ?? 'imperative';
        // Optional-chain the method itself: some tests provide a Router stub without it.
        this.lastReplaceUrl = this.router.getCurrentNavigation?.()?.extras.replaceUrl ?? false;
      } else if (event instanceof NavigationEnd) {
        this.record(event.urlAfterRedirects);
      }
    });
  }

  /**
   * Number of history steps back to reach `url` from the current page (1 = the entry
   * immediately behind), or `null` when `url` is not in the back history (a deep link
   * / first visit, where the caller must navigate forward instead). The current page
   * (stack top) is excluded, so a crumb pointing at the current URL never matches.
   */
  stepsBackTo(url: string): number | null {
    const current = this.stack.length - 1;
    for (let i = current - 1; i >= 0; i--) {
      if (this.stack[i] === url) {
        return current - i;
      }
    }
    return null;
  }

  /** Records a settled navigation onto the stack: a `popstate` realigns to the
      matched earlier entry (dropping the forward entries it moved away from); a
      `replaceUrl` navigation overwrites the stack top in place; any other trigger is a
      forward push. Consecutive duplicates are ignored. */
  private record(url: string): void {
    if (this.lastTrigger === 'popstate') {
      const idx = this.stack.lastIndexOf(url);
      if (idx >= 0) {
        this.stack.length = idx + 1;
        return;
      }
      // Forward button into an entry we pruned earlier: fall through and push it.
    } else if (this.lastReplaceUrl && this.stack.length > 0) {
      // A `replaceUrl` navigation swapped the current history entry rather than pushing —
      // mirror that by overwriting the stack top, so `stepsBackTo` counts the same number
      // of steps the browser actually has. Without this, a tab switch (which replaces the
      // URL) would inflate the stack and a crumb click would `historyGo` one step too far
      // back for every tab visited. `popstate` is handled above and never a replace.
      this.stack[this.stack.length - 1] = url;
      return;
    }
    if (this.stack[this.stack.length - 1] !== url) {
      this.stack.push(url);
    }
  }
}
